import { formatValue, OPTIMIZED_MODE_TIME_SERIES_LIMIT, UnitOptions } from '../../core';
import { ChartApi } from '../EChart';
import { EChartsDataFormat } from '../model';
import { CursorCoordinates, CursorData } from '../PanelTooltip';
import { batchDispatchNearbySeriesActions } from '../utils';
import { EMPTY_TOOLTIP_DATA, TooltipSeriesArray } from './tooltip-model';
import { isWithinPercentageRange } from './utils';

const INCREASE_NEARBY_SERIES_MULTIPLIER = 5.5; // adjusts how many series show in tooltip (higher == more series shown)
const DYNAMIC_NEARBY_SERIES_MULTIPLIER = 30; // used for adjustment after series number divisor
const SHOW_FEWER_SERIES_LIMIT = 5;

// [IMPORTANT] THIS FILE IS STILL USED IN LineChartBase.tsx (when Cloud UI migrates to TimeChartBase.tsx, we can delete)

/**
 * [DEPRECATED] Returns formatted series data for the points that are close to the user's cursor
 * Adjust yBuffer to increase or decrease number of series shown
 */
export function legacyCheckforNearbySeries(
  data: EChartsDataFormat,
  pointInGrid: number[],
  yBuffer: number,
  chartApi: ChartApi,
  unit?: UnitOptions
): TooltipSeriesArray {
  const currentNearbySeriesData: TooltipSeriesArray = [];
  const cursorX: number | null = pointInGrid[0] ?? null;
  const cursorY: number | null = pointInGrid[1] ?? null;

  if (cursorX === null || cursorY === null) {
    return currentNearbySeriesData;
  }

  const nearbySeriesIndexes: number[] = [];
  const emphasizedSeriesIndexes: number[] = [];
  const nonEmphasizedSeriesIndexes: number[] = [];
  const totalSeries = data.timeSeries.length;
  if (Array.isArray(data.xAxis) && Array.isArray(data.timeSeries)) {
    for (let seriesIndex = 0; seriesIndex < totalSeries; seriesIndex++) {
      const currentSeries = data.timeSeries[seriesIndex];
      if (currentSeries === undefined) break;
      if (currentNearbySeriesData.length >= OPTIMIZED_MODE_TIME_SERIES_LIMIT) break;

      const currentSeriesName = currentSeries.name ? currentSeries.name.toString() : '';
      const markerColor = currentSeries.color ?? '#000';
      if (Array.isArray(currentSeries.data)) {
        for (let dataIndex = 0; dataIndex < currentSeries.data.length; dataIndex++) {
          const xValue = data.xAxis[dataIndex] ?? 0;
          const yValue = currentSeries.data[dataIndex];
          // ensure null values not displayed in tooltip
          if (yValue !== undefined && yValue !== null && cursorX === dataIndex) {
            if (yValue !== '-' && cursorY <= yValue + yBuffer && cursorY >= yValue - yBuffer) {
              // show fewer bold series in tooltip when many total series
              const minPercentRange = totalSeries > SHOW_FEWER_SERIES_LIMIT ? 2 : 5;
              const percentRangeToCheck = Math.max(minPercentRange, 100 / totalSeries);
              const shouldEmphasizeText = isWithinPercentageRange({
                valueToCheck: cursorY,
                baseValue: yValue,
                percentage: percentRangeToCheck,
              });
              if (shouldEmphasizeText) {
                emphasizedSeriesIndexes.push(seriesIndex);
              } else {
                nonEmphasizedSeriesIndexes.push(seriesIndex);
                // ensure series not close to cursor are not highlighted
                chartApi.dispatchAction({
                  type: 'downplay',
                  seriesIndex,
                });
              }

              // determine whether to convert timestamp to ms, see: https://stackoverflow.com/a/23982005/17575201
              const xValueMilliSeconds = xValue > 99999999999 ? xValue : xValue * 1000;
              const formattedY = formatValue(yValue, unit);
              currentNearbySeriesData.push({
                seriesIndex,
                dataIndex,
                seriesName: currentSeriesName,
                date: xValueMilliSeconds,
                x: xValue,
                y: yValue,
                formattedY: formattedY,
                markerColor: markerColor.toString(),
                shouldEmphasizeText,
                seriesId: String(currentSeries.id),
              });
              nearbySeriesIndexes.push(seriesIndex);
            }
          }
        }
      }
    }
  }

  // Trigger emphasis and blur state for lines, select state for datapoints.
  batchDispatchNearbySeriesActions(chartApi, emphasizedSeriesIndexes, nonEmphasizedSeriesIndexes, [], []);

  return currentNearbySeriesData;
}

/**
 * [DEPRECATED] Uses mouse position to determine whether user is hovering over a chart canvas
 * If yes, convert from pixel values to logical cartesian coordinates and return all nearby series
 */
export function legacyGetNearbySeriesData({
  mousePos,
  pinnedPos,
  chartData,
  chartApi,
  unit,
  showAllSeries = false,
}: {
  mousePos: CursorData['coords'];
  pinnedPos: CursorCoordinates | null;
  chartData: EChartsDataFormat;
  chartApi: ChartApi;
  unit?: UnitOptions;
  showAllSeries?: boolean;
}) {
  if (!mousePos) return EMPTY_TOOLTIP_DATA;

  // prevents multiple tooltips showing from adjacent charts unless tooltip is pinned
  let cursorTargetMatchesChart = false;
  if (mousePos.target !== null) {
    const currentParent = (<HTMLElement>mousePos.target).parentElement;
    if (currentParent !== null) {
      const currentGrandparent = currentParent.parentElement;
      if (currentGrandparent !== null) {
        const chartEl = chartApi.getContainerEl();
        if (chartEl === currentGrandparent) {
          cursorTargetMatchesChart = true;
        }
      }
    }
  }

  // allows moving cursor inside tooltip without it fading away
  if (pinnedPos !== null) {
    mousePos = pinnedPos;
    cursorTargetMatchesChart = true;
  }

  if (cursorTargetMatchesChart === false) return [];

  const chartModel = chartApi.getChartModel();
  if (chartModel === undefined) return [];
  const yInterval = chartModel.getComponent('yAxis').axis.scale._interval;
  const totalSeries = chartData.timeSeries.length;
  const yBuffer = legacyGetYBuffer({ yInterval, totalSeries, showAllSeries });
  const pointInPixel = [mousePos.plotCanvas.x ?? 0, mousePos.plotCanvas.y ?? 0];
  if (chartApi.containPixel('grid', pointInPixel)) {
    const pointInGrid = chartApi.convertFromPixel('grid', pointInPixel);
    if (pointInGrid && pointInGrid[0] !== undefined && pointInGrid[1] !== undefined) {
      return legacyCheckforNearbySeries(chartData, pointInGrid, yBuffer, chartApi, unit);
    }
  }

  return [];
}

/*
 * [DEPRECATED] Get range to check within for nearby series to show in tooltip.
 */
export function legacyGetYBuffer({
  yInterval,
  totalSeries,
  showAllSeries = false,
}: {
  yInterval: number;
  totalSeries: number;
  showAllSeries?: boolean;
}) {
  if (showAllSeries) {
    return yInterval * 10; // roughly correlates with grid so entire canvas is searched
  }

  // never let nearby series range be less than roughly the size of a single tick
  const yBufferMin = yInterval * 0.3;

  // tooltip trigger area gets smaller with more series
  if (totalSeries > SHOW_FEWER_SERIES_LIMIT) {
    const adjustedBuffer = (yInterval * DYNAMIC_NEARBY_SERIES_MULTIPLIER) / totalSeries;
    return Math.max(yBufferMin, adjustedBuffer);
  }

  // increase multiplier to expand nearby series range
  return Math.max(yBufferMin, yInterval * INCREASE_NEARBY_SERIES_MULTIPLIER);
}
